import {
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getGuardianPrimaryNavigation,
  getGuardianSecondaryNavigation,
} from '../guardianNavigation.helpers';
import { IAppNavigationMenuItem } from '../components.interfaces';
import { DrawerToRenderProps } from './navigationDrawer.interfaces';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { pages } from '../../../utils/common';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { snakeCase } from 'lodash-es';
import {
  isNavItemActive,
  isNavItemExactActive,
} from './navigationDrawer.pathUtils';
import {
  buildSubmenuExpandedFromRoute,
  computeSubmenuToggleState,
} from './navigationDrawer.submenu.utils';

type pageStatusType = { [key: string]: boolean };

const getTranslationKey = (pagePath: string, parentRoute?: string): string => {
  const pathWithoutSlash = pagePath.replace(/^\//, '');

  if (pathWithoutSlash === 'logout') {
    return 'common.logout';
  }

  if (parentRoute) {
    const parentPath = parentRoute.replace(/^\//, '');
    return `${parentPath}.tabs.${snakeCase(pagePath.replaceAll('-', ''))}`;
  }
  return `${pathWithoutSlash}.title`;
};

const GuardianDrawerToRender: FC<DrawerToRenderProps> = (
  props: DrawerToRenderProps
) => {
  const { selectedPage, isMenuOpen } = props;
  const { translateText } = useTranslation();
  const theme = useTheme();
  const isBelowLargeScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const allPrimaryNavigation = useMemo(() => getGuardianPrimaryNavigation(), []);
  const allSecondaryNavigation = useMemo(
    () => getGuardianSecondaryNavigation(),
    []
  );

  /** Guardian nav is auth-driven (userRole === guardian), not staff RBAC. */
  const primaryNavigation = allPrimaryNavigation;
  const secondaryNavigation = allSecondaryNavigation;

  const [pagesWithSubmenu, setPagesWithSubmenu] = useState<pageStatusType>({});

  const handlePageNavigation = useCallback(
    (path: string) => {
      if (pages.auth.path === path || pages.logout.path === path || path.endsWith('/logout')) {
        sessionStorage.clear();
        localStorage.removeItem('userRole');
        localStorage.removeItem('token');
        localStorage.removeItem('instituteId');
        navigate(pages.logout.path);
        return;
      }
      navigate(path);
    },
    [navigate]
  );

  const guardianNavBase = pages.guardian?.path ?? '/guardian';

  const updateSubMenuStatus = useCallback(
    (pageToNavigate: string, isAlreadyExpanded = false) => {
      setPagesWithSubmenu({
        ...computeSubmenuToggleState(
          primaryNavigation,
          guardianNavBase,
          location.pathname,
          pageToNavigate,
          isAlreadyExpanded
        ),
        ...computeSubmenuToggleState(
          secondaryNavigation,
          guardianNavBase,
          location.pathname,
          pageToNavigate,
          isAlreadyExpanded
        ),
      });
    },
    [primaryNavigation, secondaryNavigation, guardianNavBase, location.pathname]
  );

  const onUpdatePagesWithSubmenu = useCallback(() => {
    setPagesWithSubmenu({
      ...buildSubmenuExpandedFromRoute(
        primaryNavigation,
        guardianNavBase,
        location.pathname,
        selectedPage
      ),
      ...buildSubmenuExpandedFromRoute(
        secondaryNavigation,
        guardianNavBase,
        location.pathname,
        selectedPage
      ),
    });
  }, [
    primaryNavigation,
    secondaryNavigation,
    selectedPage,
    location.pathname,
    guardianNavBase,
  ]);

  const getEachListItem = useCallback(
    (
      item: IAppNavigationMenuItem,
      indentLevel: number,
      parentRoute: string
    ) => {
      const currPage = item.pageToNavigate.replace('/', '');
      const itemFullPath =
        item.pageToNavigate === pages.logout.path
          ? pages.logout.path
          : parentRoute + item.pageToNavigate;
      const isSelected =
        isBelowLargeScreen && item.subPages
          ? isNavItemExactActive(location.pathname, itemFullPath)
          : isNavItemActive(location.pathname, itemFullPath);
      return (
        <ListItemButton
          key={item.pageToNavigate}
          selected={isSelected}
          data-pl={indentLevel}
          data-testid={`guardian-drawer-item--${currPage}`}
          data-selected={isSelected}
          sx={{
            pl: isMenuOpen
              ? `calc(14px + ${(indentLevel - 1) * 12}px)`
              : '14px',
          }}
          onClick={() => {
            if (isBelowLargeScreen && item?.subPages) {
              updateSubMenuStatus(
                item.pageToNavigate,
                pagesWithSubmenu[item.pageToNavigate]
              );
              return;
            }
            const targetPath = item.pageToNavigate === pages.logout.path
              ? pages.logout.path
              : parentRoute + item.pageToNavigate;
            item.pageToNavigate !== selectedPage &&
              handlePageNavigation(targetPath);
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          {isMenuOpen ? (
            <>
              <ListItemText
                primary={translateText(
                  getTranslationKey(item.pageToNavigate, parentRoute)
                )}
                data-testid={`guardian-drawer-item-text-${item.pageToNavigate.replace('/', '-')}`}
              />
              {isBelowLargeScreen &&
                (pagesWithSubmenu[item.pageToNavigate] ? (
                  <ExpandLess />
                ) : item.subPages ? (
                  <ExpandMore />
                ) : null)}
            </>
          ) : null}
        </ListItemButton>
      );
    },
    [
      handlePageNavigation,
      isMenuOpen,
      pagesWithSubmenu,
      selectedPage,
      location.pathname,
      translateText,
      isBelowLargeScreen,
      updateSubMenuStatus,
    ]
  );

  const getEachListItemWithSubMenu = useCallback(
    (
      item: IAppNavigationMenuItem,
      indentLevel: number,
      parentRoute: string
    ) => (
      <Fragment>
        {getEachListItem(item, indentLevel, parentRoute)}
        {isBelowLargeScreen && item?.subPages ? (
          <Collapse
            in={pagesWithSubmenu[item.pageToNavigate]}
            timeout="auto"
            unmountOnExit
            data-testid={`guardian-drawer-item-sub-menu-${item.pageToNavigate.replace('/', '-')}`}
          >
            <List component="div" disablePadding>
              {item?.subPages?.map((subPagesItem: IAppNavigationMenuItem) =>
                subPagesItem.subPages
                  ? getEachListItemWithSubMenu(
                    subPagesItem,
                    indentLevel + 1,
                    parentRoute + item.pageToNavigate
                  )
                  : getEachListItem(
                    subPagesItem,
                    indentLevel + 1,
                    parentRoute + item.pageToNavigate
                  )
              )}
            </List>
          </Collapse>
        ) : null}
      </Fragment>
    ),
    [pagesWithSubmenu, getEachListItem, isBelowLargeScreen]
  );

  useEffect(() => {
    onUpdatePagesWithSubmenu();
    // eslint-disable-next-line
  }, [
    primaryNavigation,
    secondaryNavigation,
    selectedPage,
    onUpdatePagesWithSubmenu,
  ]);

  useEffect(() => {
    if (params[Object.keys(params)[0]]) {
      handlePageNavigation(params[Object.keys(params)[0]] as string);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Box data-testid="guardian-drawer">
      <List>
        {primaryNavigation.map((primaryItem: IAppNavigationMenuItem) =>
          !primaryItem?.subPages ? (
            <Fragment key={primaryItem.pageToNavigate}>
              {getEachListItem(primaryItem, 1, pages.guardian.path)}
            </Fragment>
          ) : (
            <Fragment key={primaryItem.pageToNavigate}>
              {getEachListItemWithSubMenu(primaryItem, 1, pages.guardian.path)}
            </Fragment>
          )
        )}
      </List>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
      <List>
        {secondaryNavigation.map(
          (secondaryItem: IAppNavigationMenuItem) =>
            !secondaryItem?.subPages ? (
              <Fragment key={secondaryItem.pageToNavigate}>
                {getEachListItem(secondaryItem, 1, pages.guardian.path)}
              </Fragment>
            ) : (
              <Fragment key={secondaryItem.pageToNavigate}>
                {getEachListItemWithSubMenu(
                  secondaryItem,
                  1,
                  pages.guardian.path
                )}
              </Fragment>
            )
        )}
      </List>
    </Box>
  );
};

export default GuardianDrawerToRender;
